import { useState, useEffect, useRef } from 'react';
import './AdminResultados.css';
import { Trash2, CheckCircle2, Trophy, CalendarDays, ChevronDown, ChevronUp, Search, Mars, Venus, LayoutList, Clock, AlertTriangle, FolderArchive, Shield, Edit3, MessageSquare, X, Info, Coffee } from 'lucide-react';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, onSnapshot, getDocs, limit, setDoc, addDoc } from 'firebase/firestore';
import { Match, Team, Tournament, Player, UserProfile, Config } from '../../types';
import { AdminPendingMatchCard } from './AdminPendingMatchCard';
import { RankingService } from '../../services/rankingService';
import { migrateMatchesAddParticipantIds } from '../../services/migrationService';
import { StatsService } from '../../services/statsService';
import { TournamentService } from '../../services/tournamentService';
import { FadeInImage } from '../common/FadeInImage';
import { OrganizerLogo } from './OrganizerLogo';
import { PlayerDetailModal } from '../player/PlayerDetailModal';

interface AdminResultadosProps {
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  sedesPublicas: UserProfile[];
  config: Config;
  onMatchClick?: (m: Match) => void;
  initialTargetMatchId?: string | null;
  onTargetMatchHandled?: () => void;
  userId: string;
  userRole: string;
  targetDisputeMatchId?: string | null;
  mode?: 'tournaments_only' | 'validations_disputes' | 'full';
  isOrganizerMode?: boolean;
}

export function AdminResultados({
  players,
  db,
  getCollectionPath,
  sedesPublicas,
  config,
  onMatchClick,
  initialTargetMatchId,
  onTargetMatchHandled,
  userId,
  userRole,
  targetDisputeMatchId: propTargetDisputeMatchId,
  mode = 'full',
  isOrganizerMode
}: AdminResultadosProps) {
  // ─── Own data loading ───────────────────────────────────────────
  const [matches, setMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);

  // Load pending + disputed + recently-archived matches
  useEffect(() => {
    const matchesRef = collection(db, getCollectionPath('matches'));
    const q = query(
      matchesRef,
      where('status', 'in', ['pending', 'disputed', 'completed']),
      limit(200)
    );
    const unsub = onSnapshot(q, (snap) => {
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() } as Match));
      // Keep: all pending/disputed + recently resolved disputes for the archive tab
      const filtered = loaded.filter(m => {
        if (m.status === 'pending' || m.status === 'disputed') return true;
        if (m.status === 'completed' && m.disputeResolvedAt && !m.disputeArchivedDeleted) {
          return (Date.now() - m.disputeResolvedAt) < thirtyDaysInMs;
        }
        return false;
      });
      setMatches(filtered);

      // Collect all teamIds referenced by these matches
      const teamIds = new Set<string>();
      const tournamentIds = new Set<string>();
      filtered.forEach(m => {
        if (m.team1Id) teamIds.add(m.team1Id);
        if (m.team2Id) teamIds.add(m.team2Id);
        if (m.tournamentId) tournamentIds.add(m.tournamentId);
      });

      // Load teams in batches of 30
      const fetchTeams = async () => {
        const ids = Array.from(teamIds);
        const result: Team[] = [];
        for (let i = 0; i < ids.length; i += 30) {
          const batch = ids.slice(i, i + 30);
          const { documentId } = await import('firebase/firestore');
          const snap = await getDocs(query(collection(db, getCollectionPath('teams')), where(documentId(), 'in', batch)));
          snap.docs.forEach(d => result.push({ id: d.id, ...d.data() } as Team));
        }
        setTeams(result);
      };

      // Load tournaments
      const fetchTournaments = async () => {
        const result: Tournament[] = [];
        
        // 1. Fetch tournaments from match IDs
        const ids = Array.from(tournamentIds);
        for (let i = 0; i < ids.length; i += 30) {
          const batch = ids.slice(i, i + 30);
          const { documentId } = await import('firebase/firestore');
          const snap = await getDocs(query(collection(db, getCollectionPath('tournaments')), where(documentId(), 'in', batch)));
          snap.docs.forEach(d => {
            const t = { id: d.id, ...d.data() } as Tournament;
            if (!result.some(existing => existing.id === t.id)) result.push(t);
          });
        }
        
        // 2. Fetch all active tournaments for this user (or all if superadmin)
        try {
          let tQuery;
          if (userRole === 'superadmin') {
             // For superadmin, we fetch all. We can't do != in Firestore without indexes easily if we don't have them, so fetch all and filter in memory. (Limit to avoid huge loads, or just rely on 'isArchived' == false if indexed, but let's just fetch all un-archived).
             // To be safe without indexes, we just fetch all tournaments (there usually aren't tens of thousands yet).
             tQuery = query(collection(db, getCollectionPath('tournaments')));
          } else {
             tQuery = query(collection(db, getCollectionPath('tournaments')), where('organizerId', '==', adminClubId || userId));
          }
          const tSnap = await getDocs(tQuery);
          tSnap.docs.forEach(d => {
            const t = { id: d.id, ...d.data() } as Tournament;
            if (!t.isArchived && t.status !== 'Finalizado' && t.status !== 'Lobby' && t.status !== 'Inscripción') {
              if (!result.some(existing => existing.id === t.id)) {
                result.push(t);
              }
            }
          });
        } catch (e) {
          console.error("Error fetching active tournaments", e);
        }

        setTournaments(result);
      };

      const doFetch = async () => {
        if (teamIds.size > 0) await fetchTeams();
        await fetchTournaments();
        setIsInitialLoading(false);
      };
      doFetch();
    });
    return () => unsub();
  }, [db, getCollectionPath]);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [completedMatchesIds, setCompletedMatchesIds] = useState<string[]>([]);
  const [isMigrating, setIsMigrating] = useState(false);
  const [isMigratingStats, setIsMigratingStats] = useState(false);
  const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

  const [adminClubId, setAdminClubId] = useState<string | null>(null);
  const [clubIdResolved, setClubIdResolved] = useState(userRole === 'superadmin');
  const [activeTab, setActiveTab] = useState<'pendientes' | 'disputas' | 'archivadas' | 'categoria'>(
    mode === 'validations_disputes' ? 'disputas' : 'pendientes'
  );
  
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'validations_disputes' && activeTab !== 'disputas' && activeTab !== 'archivadas') {
      setActiveTab('disputas');
    } else if (mode === 'tournaments_only' && activeTab !== 'pendientes') {
      setActiveTab('pendientes');
    }
  }, [mode]);

  const isMatchForOrganizer = (m: Match) => {
    if (userRole === 'superadmin') return true;

    if (isOrganizerMode) {
      if (m.organizerId === userId) return true;
      const t = tournaments.find(t => t.id === m.tournamentId);
      return t ? t.organizerId === userId : false;
    }

    const t = m.tournamentId ? tournaments.find(t => t.id === m.tournamentId) : null;
    
    const matchBelongsToClub = 
      (m as any).clubId === userId || 
      m.organizerId === userId || 
      (adminClubId && (m as any).clubId === adminClubId) || 
      (adminClubId && m.organizerId === adminClubId);

    const tournamentBelongsToClub = t ? (
      t.organizerId === userId || 
      (adminClubId && t.organizerId === adminClubId) || 
      (t as any).clubId === userId || 
      (adminClubId && (t as any).clubId === adminClubId)
    ) : false;

    return matchBelongsToClub || tournamentBelongsToClub;
  };

  const pending = matches.filter((m) => {
    if (!isMatchForOrganizer(m) || !m.tournamentId) return false;
    const t = tournaments.find(t => t.id === m.tournamentId);
    if (t && (t.isArchived || t.status === 'Finalizado')) return false;
    return (m.status === 'pending' || m.status === 'disputed' || m.status === 'impugned' || completedMatchesIds.includes(m.id));
  });

  const disputed = matches.filter((m) => {
    if (!isMatchForOrganizer(m) || m.status !== 'disputed' || !m.tournamentId) return false;
    const t = tournaments.find(t => t.id === m.tournamentId);
    if (t && (t.isArchived || t.status === 'Finalizado')) return false;
    return true;
  });

  const archivedDisputes = matches.filter((m) => {
    if (!isMatchForOrganizer(m) || m.status !== 'completed' || !m.disputeResolvedAt || m.disputeArchivedDeleted || !m.tournamentId) return false;
    const t = tournaments.find(t => t.id === m.tournamentId);
    if (t && (t.isArchived || t.status === 'Finalizado')) return false;
    return (Date.now() - m.disputeResolvedAt) < thirtyDaysInMs;
  });
  // true once the clubId lookup has completed (either found or confirmed null)

  // Load config — wait until clubIdResolved so we always read from the correct doc
  useEffect(() => {
    if (!clubIdResolved) return; // don't subscribe until we know the club ID
    const isSuper = userRole === 'superadmin';
    // For club admins / co-admins: always use the club owner's doc (adminClubId).
    // Fall back to userId only if the user IS the club owner (no separate clubId).
    const targetDocId = isSuper ? 'main' : (adminClubId || userId);
    const docRef = isSuper
      ? doc(db, getCollectionPath('settings'), targetDocId)
      : doc(db, getCollectionPath('users'), targetDocId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
      }
    }, (err) => console.error("Error loading auto approve config:", err));

    return () => unsubscribe();
  }, [db, getCollectionPath, userId, userRole, adminClubId, clubIdResolved]);




  const [localSuccessMsg, setLocalSuccessMsg] = useState('');
  const [confirmClearPending, setConfirmClearPending] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (userRole === 'superadmin') {
      setClubIdResolved(true); // superadmin never needs a clubId lookup
      return;
    }
    if (!userId) return;
    const fetchAdminClub = async () => {
      try {
        const uDoc = await getDoc(doc(db, 'users', userId));
        if (uDoc.exists()) {
          // co_admin has a clubId pointing to the club owner; admin IS the club owner
          setAdminClubId(uDoc.data()?.clubId || null);
        }
      } catch (e) {
        console.error('Error fetching admin club:', e);
      } finally {
        setClubIdResolved(true); // mark resolved regardless of outcome
      }
    };
    fetchAdminClub();
  }, [userId, userRole, db]);

      const getPlayerDetails = (id: string): { name: string; photoUrl: string; category: string; isVerified: boolean } => {
    if (id.startsWith('guest_')) {
      const parts = id.split('_');
      const name = parts.length >= 3 ? parts.slice(2).join(' ') : 'Jugador Invitado';
      return { name, photoUrl: '', category: 'Invitado', isVerified: false };
    }
    const p = players.find(x => x.id === id);
    return {
      name: p?.name || 'Desconocido',
      photoUrl: p?.photoUrl || '',
      category: p?.category || 'Sin Categoría',
      isVerified: p?.isValidated || false
    };
  };

  const [expandedBanners, setExpandedBanners] = useState<string[]>([]);
  const [selectedBanner, setSelectedBanner] = useState<string | null>(null);
  const [targetDisputeMatchId, setTargetDisputeMatchId] = useState<string | null>(null);

  // Listen for navigation events from notification clicks
  useEffect(() => {
    const handleNavigateToDisputas = (e: CustomEvent) => {
      const matchId = e.detail as string | undefined;
      setSearchQuery(''); // Clear search to ensure match is visible
      setActiveTab('disputas');
      if (matchId) {
        setTargetDisputeMatchId(matchId);
      }
    };
    
    window.addEventListener('navigateToDisputas', handleNavigateToDisputas as EventListener);
    return () => {
      window.removeEventListener('navigateToDisputas', handleNavigateToDisputas as EventListener);
    };
  }, []);

  useEffect(() => {
    if (isInitialLoading || tournaments.length === 0) return;
    const pending = sessionStorage.getItem('pendingZoneResult');
    if (pending) {
      try {
        const { tournamentId, zoneName } = JSON.parse(pending);
        const tournament = tournaments.find(t => t.id === tournamentId);
        if (tournament) {
          sessionStorage.removeItem('pendingZoneResult');
          setActiveTab('pendientes');
          setTimeout(() => {
            const zoneKey = `${tournament.name}::${zoneName}`;
            setExpandedBanners([tournament.name, zoneKey]);
            setSelectedBanner(zoneKey);
          }, 100);
        }
      } catch (e) {}
    }
  }, [isInitialLoading, tournaments]);

  // When targetDisputeMatchId is set, expand the right banners and scroll to the card
  useEffect(() => {
    const effectiveMatchId = initialTargetMatchId || targetDisputeMatchId;
    if (!effectiveMatchId) return;
    
    const tournamentMatch = matches.find(m => m.id === effectiveMatchId);
    const match = tournamentMatch;
    
    if (!match) return;

    // Switch tab if needed
    if (match.status === 'disputed' && activeTab !== 'disputas') {
      setActiveTab('disputas');
      return; // Wait for next effect run with updated activeTab
    }
    
    const tName = match.tournamentName || 'Torneo';
    const rName = match.round || '';
    const isElimination = rName.toLowerCase().includes('8vos') ||
      rName.toLowerCase().includes('4tos') ||
      rName.toLowerCase().includes('semi') ||
      rName.toLowerCase().includes('final') ||
      rName.toLowerCase().includes('eliminaci') ||
      rName.toLowerCase().includes('3er');
    const tournament = tournaments.find(t => t.id === match.tournamentId);
    const isLiga = tournament?.type === 'liga';
    const zName = isLiga && match.zoneName && match.zoneName !== 'Zona Única' ? match.zoneName : null;

    // Determine which banners to expand
    const toExpand: string[] = [tName];
    if (isElimination) toExpand.push(`${tName}::Elimination`);
    else if (zName) toExpand.push(`${tName}::${zName}`);

    setExpandedBanners(prev => {
      const next = [...prev];
      toExpand.forEach(k => { if (!next.includes(k)) next.push(k); });
      return next;
    });
    setSelectedBanner(tName);

    // Scroll to the card after a short delay to allow render
    const scrollTimeout = setTimeout(() => {
      const el = document.querySelector(`[data-dispute-id="${effectiveMatchId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('dispute-highlight');
        setTimeout(() => el.classList.remove('dispute-highlight'), 2500);
        
        // Success! Clear the targets
        setTargetDisputeMatchId(null);
        if (initialTargetMatchId && onTargetMatchHandled) {
          onTargetMatchHandled();
        }
      }
    }, 600); 

    return () => clearTimeout(scrollTimeout);
  }, [targetDisputeMatchId, initialTargetMatchId, matches, tournaments, activeTab]);

  const getTeamObj = (id: string) => teams.find((t) => t.id === id) || { name: 'Desconocido', photoUrl: '' };

  const toggleBanner = (id: string) => {
    setExpandedBanners(prev => {
      const isAlreadyExpanded = prev.includes(id);

      if (isAlreadyExpanded) {
        setCompletedMatchesIds(prevIds => prevIds.filter(mid => {
          const match = matches.find(m => m.id === mid);
          if (!match) return false;
          const tName = match.tournamentName || 'Torneo';
          const zName = match.zoneName;
          const zoneKey = `${tName}::${zName}`;
          if (id === tName) return false;
          if (id === zoneKey) return false;
          return true;
        }));
      }

      let nextExpanded: string[] = [];
      if (isAlreadyExpanded) {
        if (!id.includes('::')) {
          nextExpanded = prev.filter(key => key !== id && !key.startsWith(`${id}::`));
        } else {
          nextExpanded = prev.filter(key => key !== id);
        }
      } else {
        if (id.includes('::')) {
          const tournamentName = id.split('::')[0];
          nextExpanded = [...prev, id];
          if (!prev.includes(tournamentName)) nextExpanded.push(tournamentName);
        } else {
          nextExpanded = [...prev, id];
        }
      }
      setSelectedBanner(nextExpanded.includes(id) ? id : null);
      return nextExpanded;
    });
  };

  const handleUpdateDateTime = async (matchId: string, date: string, time: string) => {
    await updateDoc(doc(db, getCollectionPath('matches'), matchId), { date, time });
    setMatches(prev => prev.map(m => m.id === matchId ? { ...m, date, time } : m));
  };

  const handleSaveResult = async (m: Match, s1: string, s2: string, s3: string, wId: string) => {
    const score = [s1, s2, s3].filter(Boolean).join(' ');

    // Auto-compute participantIds from the teams so every new match has it
    const t1 = teams.find(t => t.id === m.team1Id);
    const t2 = teams.find(t => t.id === m.team2Id);
    const computedParticipantIds = [
      t1?.player1Id, t1?.player2Id, t1?.player3Id,
      t2?.player1Id, t2?.player2Id, t2?.player3Id
    ].filter(Boolean) as string[];

    let winnerPlayerIds: string[] = [];
    if (wId === m.team1Id && t1) {
      winnerPlayerIds = [t1.player1Id, t1.player2Id, t1.player3Id || ''].filter(Boolean);
    } else if (wId === m.team2Id && t2) {
      winnerPlayerIds = [t2.player1Id, t2.player2Id, t2.player3Id || ''].filter(Boolean);
    }

    const matchPayload: any = {
      status: 'completed',
      s1: s1 || '',
      s2: s2 || '',
      s3: s3 || '',
      score: score || '',
      winnerId: wId || null,
      winnerPlayerIds,
      ...(m.status === 'disputed' || !!m.impugnedAt ? { disputeResolvedAt: Date.now(), disputeResolvedById: userId } : {}),
      ...(computedParticipantIds.length > 0 && !m.participantIds ? { participantIds: computedParticipantIds } : {})
    };

    setCompletedMatchesIds(prev => [...prev, m.id]);
    await updateDoc(doc(db, getCollectionPath('matches'), m.id), matchPayload);

    const updatedMatch = { ...m, ...matchPayload } as Match;

    if (wId) {
      await RankingService.processMatchPoints(updatedMatch);
      
      // Update player stats asynchronously
      StatsService.processMatchStats(updatedMatch).catch(e => console.error("Error updating player stats", e));
    }

    if (m.tournamentId) {
      await TournamentService.updateTournamentAfterMatch(updatedMatch, s1, s2, s3, wId);
    }
  };

  const handleClearAllPending = async () => {
    const pendingToClear = matches.filter((m) => m.status === 'pending');
    for (const m of pendingToClear) {
      await deleteDoc(doc(db, getCollectionPath('matches'), m.id));
    }
    setConfirmClearPending(false);
    setLocalSuccessMsg('Se han eliminado todos los partidos pendientes.');
    setTimeout(() => setLocalSuccessMsg(''), 4000);
  };

  const handleDeletePendingMatch = async (matchId: string) => {
    const match = matches.find(m => m.id === matchId);
    if (!match) return;

    if (match.status === 'disputed') {
      try {
        await updateDoc(doc(db, getCollectionPath('matches'), matchId), {
          status: 'pending',
          s1: null,
          s2: null,
          s3: null,
          reportedResult: null,
          reportedAt: null,
          reportedById: null,
          impugnedAt: null,
          impugnedById: null,
          impugnedReason: null
        });
        
        // Notify players
        const pIds = match.participantIds || [];
        if (pIds.length === 0) {
          const t1 = getTeamObj(match.team1Id) as any;
          const t2 = getTeamObj(match.team2Id) as any;
          if (t1) pIds.push(t1.player1Id, t1.player2Id, t1.player3Id);
          if (t2) pIds.push(t2.player1Id, t2.player2Id, t2.player3Id);
        }
        const validPids = pIds.filter((id: any) => id);
        for (const pid of validPids) {
          try {
            await addDoc(collection(db, getCollectionPath('notifications')), {
              userId: pid,
              type: 'match_dispute_rejected',
              title: 'Disputa Desestimada',
              body: 'El administrador ha resuelto desestimar la disputa ofreciendo una segunda instancia para que puedan ponerse de acuerdo.',
              matchId: match.id,
              read: false,
              createdAt: Date.now()
            });
          } catch(e) {}
        }

        setLocalSuccessMsg('Disputa desestimada. El partido vuelve a estar pendiente.');
        setTimeout(() => setLocalSuccessMsg(''), 4000);
      } catch (error) {
        console.error("Error rejecting dispute:", error);
        alert("Hubo un error al desestimar la disputa.");
      }
    } else {
      await deleteDoc(doc(db, getCollectionPath('matches'), matchId));
      setLocalSuccessMsg('Partido anulado de la grilla.');
      setTimeout(() => setLocalSuccessMsg(''), 4000);
    }
  };

  const handleDeleteArchivedDispute = async (matchId: string) => {
    try {
      await updateDoc(doc(db, getCollectionPath('matches'), matchId), {
        disputeArchivedDeleted: true
      });
      setLocalSuccessMsg('Disputa eliminada del archivo.');
      setTimeout(() => setLocalSuccessMsg(''), 4000);
    } catch (e) {
      console.error('Error deleting archived dispute', e);
    }
  };

  const activeMatchesList = activeTab === 'pendientes' ? pending : activeTab === 'disputas' ? disputed : archivedDisputes;

  const filteredMatches = activeMatchesList.filter(m => {
    const query = searchQuery.toLowerCase();
    const t1 = getTeamObj(m.team1Id).name.toLowerCase();
    const t2 = getTeamObj(m.team2Id).name.toLowerCase();
    const tournament = m.tournamentName?.toLowerCase() || '';
    return t1.includes(query) || t2.includes(query) || tournament.includes(query);
  });

  const prevSearch = useRef(searchQuery);

  useEffect(() => {
    if (searchQuery === prevSearch.current) return;
    prevSearch.current = searchQuery;
    if (!searchQuery) {
      setExpandedBanners([]);
      setSelectedBanner(null);
      return;
    }
    const matchedKeys = new Set<string>();
    filteredMatches.forEach(m => {
      const tName = m.tournamentName || 'Torneo';
      matchedKeys.add(tName);
      const rName = m.round || '';
      const isElim = rName.toLowerCase().includes('8vos') ||
        rName.toLowerCase().includes('4tos') ||
        rName.toLowerCase().includes('semi') ||
        rName.toLowerCase().includes('final') ||
        rName.toLowerCase().includes('eliminaci') ||
        rName.toLowerCase().includes('3er');
      if (isElim) matchedKeys.add(`${tName}::Elimination`);
      else if (m.zoneName && m.zoneName !== 'Zona Única') matchedKeys.add(`${tName}::${m.zoneName}`);
    });
    const keysArray = Array.from(matchedKeys);
    setExpandedBanners(keysArray);
    const topLevel = keysArray.filter(k => !k.includes('::'));
    if (topLevel.length === 1) setSelectedBanner(topLevel[0]);
    else setSelectedBanner(null);
  }, [searchQuery, filteredMatches.length]);

  const hierarchy: any = {};
  
  if (activeTab === 'pendientes' && !searchQuery) {
    const myTournaments = tournaments.filter(t => {
      if (t.isArchived || t.status === 'Finalizado' || t.status === 'Lobby' || t.status === 'Inscripción') return false;
      if (userRole === 'superadmin') return true;
      return t.organizerId === userId || 
        (adminClubId && t.organizerId === adminClubId) || 
        (t as any).clubId === userId || 
        (adminClubId && (t as any).clubId === adminClubId);
    });
    
    myTournaments.forEach(t => {
      hierarchy[t.name] = { isLiga: t.type === 'liga', zones: {}, rounds: {}, elimination: {} };
    });
  }

  filteredMatches.forEach(m => {
    const tName = m.tournamentName || 'Torneo';
    const tournament = tournaments.find(t => t.id === m.tournamentId);
    const isLiga = tournament?.type === 'liga';
    const zName = isLiga && m.zoneName && m.zoneName !== 'Zona Única' ? m.zoneName : null;
    const rName = m.round || 'Sin Fecha';
    const isElimination = rName.toLowerCase().includes('8vos') ||
      rName.toLowerCase().includes('4tos') ||
      rName.toLowerCase().includes('semi') ||
      rName.toLowerCase().includes('final') ||
      rName.toLowerCase().includes('eliminaci') ||
      rName.toLowerCase().includes('3er');
    if (!hierarchy[tName]) hierarchy[tName] = { isLiga, zones: {}, rounds: {}, elimination: {} };
    if (isElimination) {
      if (!hierarchy[tName].elimination[rName]) hierarchy[tName].elimination[rName] = [];
      hierarchy[tName].elimination[rName].push(m);
    } else if (zName) {
      if (!hierarchy[tName].zones[zName]) hierarchy[tName].zones[zName] = {};
      if (!hierarchy[tName].zones[zName][rName]) hierarchy[tName].zones[zName][rName] = [];
      hierarchy[tName].zones[zName][rName].push(m);
    } else {
      if (!hierarchy[tName].rounds[rName]) hierarchy[tName].rounds[rName] = [];
      hierarchy[tName].rounds[rName].push(m);
    }
  });

  return (
    <div className="space-y-6 relative">
      {/* Centered Title Area */}
      {mode === 'full' && !isOrganizerMode && (
        <div className="flex flex-col items-center justify-center text-center mb-8">
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-widest theme-font-secondary flex items-center justify-center gap-3 drop-shadow-[0_0_15px_rgba(var(--glow-rgb),0.1)]">
            <LayoutList className="theme-primary-text" size={24} />
            Resultados y validaciones
          </h3>
          <div className="h-0.5 w-16 theme-primary-bg rounded-full mt-2.5 opacity-80 shadow-[0_0_8px_rgba(var(--glow-rgb),0.4)]" />
          <div className="flex gap-4 mt-4">
            {userRole === 'superadmin' && (
              <>
                <button
                  disabled={isMigrating}
                  onClick={async () => {
                    if (!window.confirm('¿Ejecutar migración de partidos? Esto agregará participantIds a todos los partidos para permitir paginación.')) return;
                    setIsMigrating(true);
                    const res = await migrateMatchesAddParticipantIds();
                    setIsMigrating(false);
                    if (res.success) alert(`Migración completada. Partidos actualizados: ${res.updated}`);
                    else alert('Error en la migración');
                  }}
                  className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-xl font-bold text-xs uppercase"
                >
                  {isMigrating ? 'Migrando...' : 'Migrar Partidos (Fase 2)'}
                </button>

                <button
                  disabled={isMigratingStats}
                  onClick={async () => {
                    if (!window.confirm('¿Recalcular todas las estadísticas de jugadores? Esto generará los documentos playerStats requeridos para la Fase 3.')) return;
                    setIsMigratingStats(true);
                    try {
                      for (const player of players) {
                        await StatsService.recalculatePlayerStats(player.id);
                      }
                      alert('Migración de Estadísticas Completada.');
                    } catch (e) {
                      alert('Error durante la migración de stats.');
                    } finally {
                      setIsMigratingStats(false);
                    }
                  }}
                  className="px-4 py-2 bg-violet-500/20 text-violet-400 border border-violet-500/50 rounded-xl font-bold text-xs uppercase"
                >
                  {isMigratingStats ? 'Calculando...' : 'Migrar Estadísticas (Fase 3)'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tabs and Search Row */}
      {mode !== 'validations_disputes' && (
        <div className={`flex flex-col lg:flex-row justify-center items-center gap-4 mb-6 w-full pt-2`}>
        {/* Left Side: Tabs Group */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap w-full lg:w-auto">
          {mode !== 'validations_disputes' && mode !== 'tournaments_only' && !isOrganizerMode && (
            <button
              onClick={() => setActiveTab('pendientes')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === 'pendientes'
                  ? 'theme-primary-bg text-black shadow-xl scale-105 border border-theme-primary/30'
                  : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              <Clock size={14} className={activeTab === 'pendientes' ? 'text-black' : 'theme-primary-text opacity-70'} />
              Torneos ({pending.length})
            </button>
          )}

          {mode !== 'tournaments_only' && (
            <>


              <div className="flex items-center gap-2 bg-white/5 p-1 rounded-2xl border border-white/5 shrink-0">
                <button
                  onClick={() => setActiveTab('disputas')}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-[10px] md:text-[10.5px] font-black uppercase tracking-widest transition-all ${
                    (activeTab === 'disputas' || activeTab === 'archivadas')
                      ? 'bg-red-500 text-white shadow-lg scale-102 border border-red-400/20'
                      : 'text-white/40 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <AlertTriangle size={12} className={(activeTab === 'disputas' || activeTab === 'archivadas') ? 'text-white' : 'text-red-500/60'} />
                  Disputas ({disputed.length})
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Side: Search Box */}
        {mode !== 'validations_disputes' && (
          <div className={`relative w-full sm:max-w-md lg:w-96 shrink-0 mx-auto ${mode !== 'tournaments_only' ? 'lg:mx-0' : ''}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30" size={16} />
            <input
              type="text"
              placeholder="Buscar por equipo o torneo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl py-2 pl-10 pr-4 focus:theme-primary-border outline-none transition-all placeholder:opacity-40 text-[10px] md:text-[11px] font-black uppercase tracking-wider"
            />
          </div>
        )}
      </div>
      )}

      
        {activeTab === 'archivadas' && (
        <div className="text-[10px] md:text-xs text-white/50 italic mb-4 px-2">
          Las disputas archivadas se pueden eliminar manualmente, o el sistema las eliminará automáticamente a los 30 días de archivadas.
        </div>
      )}


      {localSuccessMsg && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 theme-primary-bg text-black px-6 py-3 md:px-8 md:py-4 rounded-full font-black shadow-2xl z-[9999] flex items-center animate-in fade-in slide-in-from-top-8 text-sm md:text-base w-max max-w-[90vw] border-2 border-black/20">
          <CheckCircle2 className="mr-2 md:mr-3 shrink-0" size={24} />
          <span className="truncate">{localSuccessMsg}</span>
        </div>
      )}

      {activeTab === 'categoria' && (
        <AdminCategoryValidationSection
          players={players}
          db={db}
          getCollectionPath={getCollectionPath}
          userId={userId}
          userRole={userRole}
          sedesPublicas={sedesPublicas}
          setLocalSuccessMsg={setLocalSuccessMsg}
          adminClubId={adminClubId}
        />
      )}

      {(activeTab === 'pendientes' || activeTab === 'disputas' || activeTab === 'archivadas') && (
        <>
          {isInitialLoading && (
            <div className="flex justify-center items-center py-20 opacity-50">
              <div className="w-8 h-8 border-4 border-theme-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {!isInitialLoading && Object.keys(hierarchy).length === 0 && searchQuery && (
            <div className="theme-bg-card border border-white/5 rounded-[var(--radius-large)] p-16 text-center shadow-xl backdrop-blur-md">
              <Trophy className="w-16 h-16 opacity-5 mx-auto mb-6" />
              <p className="text-xl opacity-30 font-black uppercase tracking-[0.3em]">
                Cero coincidencias
              </p>
            </div>
          )}

          {(activeTab === 'disputas' || activeTab === 'archivadas') && (
            <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
              <button
                onClick={() => setActiveTab('disputas')}
                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'disputas' 
                    ? 'bg-red-500 text-white shadow-lg border border-red-400/20' 
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white border border-white/5'
                }`}
              >
                Activas ({disputed.length})
              </button>
              <button
                onClick={() => setActiveTab('archivadas')}
                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === 'archivadas' 
                    ? 'bg-white/15 text-white shadow-lg border border-white/10' 
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white border border-white/5'
                }`}
              >
                Archivadas ({archivedDisputes.length})
              </button>
            </div>
          )}

          {activeTab === 'disputas' && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6 mb-8 text-sm text-white/70 shadow-lg backdrop-blur-sm">
              <h3 className="font-black text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2 text-base">
                <Info size={18} />
                Guía de Resolución de Disputas
              </h3>
              <p className="mb-5 text-white/80">Aquí se muestra una guía de lo que puedes hacer como administrador para poder gestionar el conflicto entre los jugadores.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/20 p-3.5 rounded-lg border border-white/10 flex flex-col gap-2">
                  <div className="flex items-center gap-2 font-bold text-white"><MessageSquare size={14} className="text-white/60" /> 1. Chat</div>
                  <p className="text-xs opacity-80">Hablá con los jugadores para entender qué pasó y determinar cual es el motivo de la disputa.</p>
                </div>
                
                <div className="bg-black/20 p-3.5 rounded-lg border border-amber-500/20 flex flex-col gap-2">
                  <div className="flex items-center gap-2 font-bold text-amber-400"><Edit3 size={14} /> 2. Modificar & Validar</div>
                  <p className="text-xs opacity-80">Una vez sepas el resultado real, seleccionalo en los casilleros y dale a <span className="text-emerald-400 font-bold">Validar</span>. Esto repartirá los puntos y cerrará la disputa.</p>
                </div>
                
                <div className="bg-black/20 p-3.5 rounded-lg border border-white/10 flex flex-col gap-2">
                  <div className="flex items-center gap-2 font-bold text-white"><X size={14} className="text-white/60" /> 3. Desestimar disputa</div>
                  <p className="text-xs opacity-80">Hacé clic en la "X" para desestimar la disputa. Esto devuelve la posibilidad a los jugadores de volver a ponerse de acuerdo entre sí y cargar el resultado nuevamente.</p>
                </div>
              </div>
            </div>
          )}

          {!isInitialLoading && (
            <div className="space-y-4">
              {Object.entries(hierarchy).map(([tName, tData]: [string, any]) => {
              const isExpanded = expandedBanners.includes(tName);
              const isSelected = selectedBanner === tName;
              const tournamentObj = tournaments.find(t => t.name === tName || t.id === tName);
              const sede = sedesPublicas.find(s => s.uid === tournamentObj?.organizerId);
              const playerOrganizer = !sede ? players.find(p => p.id === tournamentObj?.organizerId) : null;
              
              const isSuperAdminOrganizer = sede?.role === 'superadmin' || 
                tournamentObj?.organizerId === 'admin' || 
                tournamentObj?.organizerId === 'main_club' || 
                tournamentObj?.organizerId === 'agunavarro1989@gmail.com' ||
                playerOrganizer?.email === 'agunavarro1989@gmail.com' ||
                playerOrganizer?.name?.toLowerCase().includes('agustin navarro');

              const organizerName = isSuperAdminOrganizer ? config.pageName : (sede ? sede.name : (playerOrganizer ? (playerOrganizer.organizerProfile?.name || playerOrganizer.name) : 'Torneo'));
              const organizerPhoto = isSuperAdminOrganizer ? config.logoUrl : (sede?.photoUrl || playerOrganizer?.organizerProfile?.logoUrl || playerOrganizer?.photoUrl || null);

              return (
                <div key={tName} className="space-y-3">
                  <div
                    data-admin-fixture-tournament-id={tName}
                    className={`group border rounded-2xl relative isolate mx-2 transition-all duration-300 ${!isExpanded ? 'hover:scale-[1.01]' : ''} ${isExpanded
                      ? 'border-white/50 shadow-[0_4px_10px_rgba(0,0,0,0.35)] z-20'
                      : 'border-white/5 hover:border-white/20 shadow-[0_4px_10px_rgba(0,0,0,0.35)]'}`}
                  >
                    <div 
                      className={`bg-[#5fa0c4]/25 transition-colors duration-300 group-hover:bg-[#5fa0c4]/35 relative overflow-hidden flex items-center justify-between cursor-pointer ring-1 ring-white/5 ${isExpanded ? 'rounded-t-2xl pt-4 px-4 md:pt-5 md:px-5 pb-4 md:pb-5' : 'p-4 md:p-5 rounded-2xl'}`}
                      onClick={() => toggleBanner(tName)}
                    >
                    {/* Status Tags */}
                    {tournamentObj && (
                      <div className="absolute top-0 right-0 flex z-30 pointer-events-none">
                        {(tournamentObj.status === 'Lobby' || tournamentObj.status === 'Registro' || tournamentObj.status === 'Borrador' || !tournamentObj.status) && (
                          <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl bg-lime-500/20 text-lime-400 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-lime-500/30 shadow-sm backdrop-blur-md">
                            <div className="w-1.5 h-1.5 rounded-full bg-lime-500/80 shadow-[0_0_8px_rgba(132,204,22,0.3)] animate-pulse" />
                            INSCRIPCIÓN
                          </span>
                        )}
                        {(tournamentObj.status === 'En Juego' || tournamentObj.status === 'Fase de Grupos' || tournamentObj.status === 'Eliminatorias') && (
                          <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl bg-white/10 text-white/90 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-white/20 shadow-sm backdrop-blur-md">
                            <div className="w-1.5 h-1.5 rounded-full bg-lime-500 shadow-[0_0_8px_rgba(132,204,22,0.4)]" />
                            {tournamentObj.status?.toUpperCase()}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="flex flex-col items-center justify-center gap-1 shrink-0 pr-3 md:pr-4 border-r border-white/10">
                        <span className="text-[6px] md:text-[7px] font-black uppercase tracking-[0.2em] text-white/40">Organiza:</span>
                        
                        <OrganizerLogo 
                          logoUrl={organizerPhoto}
                          configLogoUrl={config.logoUrl}
                          organizerName={organizerName}
                          isSuperAdminOrganizer={isSuperAdminOrganizer}
                        />
                        
                        <span className="text-[7px] md:text-[8px] font-black uppercase tracking-wider text-theme-primary truncate w-full text-center max-w-[60px] md:max-w-[80px]">
                          {organizerName}
                        </span>
                      </div>
                      <div className="flex flex-col min-w-0 gap-1.5 pt-2 md:pt-0 flex-1">
                        <h3 className="text-lg md:text-xl font-black tracking-tight uppercase theme-font-secondary italic leading-tight break-words w-full [text-wrap:balance]">{tName}</h3>
                        {tournamentObj && (
                          <div className="flex flex-wrap items-center gap-2.5">
                            {tournamentObj.category && <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-white/30 text-white shrink-0">{tournamentObj.category.toLowerCase() === 'libre' ? 'CAT. LIBRE' : tournamentObj.category}</span>}
                            {tournamentObj.gender && (
                              <span className={`px-2 py-0.5 rounded-md flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border ${
                                tournamentObj.gender.toLowerCase() === 'masculino' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                tournamentObj.gender.toLowerCase() === 'femenino' ? 'bg-violet-500/10 text-violet-400 border-violet-500/20' :
                                (tournamentObj.gender.toLowerCase() === 'sin_restriccion' || tournamentObj.gender.toLowerCase() === 'sin restricción' || tournamentObj.gender.toLowerCase() === 'sin restriccion') ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                'bg-yellow-500/10 text-white border-yellow-500/20'
                              }`}>
                                {tournamentObj.gender.toLowerCase() === 'masculino' && <Mars size={10} />}
                                {tournamentObj.gender.toLowerCase() === 'femenino' && <Venus size={10} />}
                                {(!['masculino', 'femenino', 'sin_restriccion', 'sin restricción', 'sin restriccion'].includes(tournamentObj.gender.toLowerCase())) && (
                                  <div className="flex -space-x-1 text-yellow-400">
                                    <Mars size={10} />
                                    <Venus size={10} />
                                  </div>
                                )}
                                {tournamentObj.gender.toLowerCase() === 'masculino' ? 'Caballeros' : 
                                 tournamentObj.gender.toLowerCase() === 'femenino' ? 'Damas' : 
                                 (tournamentObj.gender.toLowerCase() === 'sin_restriccion' || tournamentObj.gender.toLowerCase() === 'sin restricción' || tournamentObj.gender.toLowerCase() === 'sin restriccion') ? 'Género Libre' : 'Mixto'}
                              </span>
                            )}
                            {(tournamentObj.startedAt || tournamentObj.startDate) && (
                              <div className="flex items-center gap-1 opacity-40">
                                <CalendarDays size={10} className="theme-primary-text" />
                                <span className="text-[9px] font-black uppercase tracking-wider">
                                  {tournamentObj.startedAt 
                                    ? `Inicio: ${new Date(tournamentObj.startedAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}`
                                    : `Est: ${tournamentObj.startDate}`
                                  }
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      {isExpanded ? <ChevronUp size={20} className="theme-primary-text" /> : <ChevronDown size={20} className="opacity-40" />}
                    </div>
                  </div>
                  </div>

                  <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${isExpanded ? 'max-h-[20000px]' : 'max-h-0'}`}>
                    <div className="bg-slate-900/50 px-4 md:px-5 pb-4 md:pb-5 pt-4 rounded-b-2xl border-t border-white/10">
                      <div className="pl-4 md:pl-8 space-y-4 border-l-2 border-white/5 ml-1 md:ml-2">
                      {Object.keys(tData.elimination || {}).length > 0 && (
                        (() => {
                          const elimKey = `${tName}::Elimination`;
                          const isElimExpanded = expandedBanners.includes(elimKey);
                          const isElimSelected = selectedBanner === elimKey;
                          return (
                            <div key={elimKey} className="space-y-4">
                              <div
                                className={`px-5 py-3 rounded-xl flex items-center justify-between border cursor-pointer transition-all ${isElimSelected ? 'theme-primary-border bg-theme-primary/10 shadow-[0_0_15px_rgba(var(--glow-rgb), 0.1)]' : 'bg-white/5 border-white/10 hover:bg-white/10 opacity-70 hover:opacity-100'}`}
                                onClick={() => toggleBanner(elimKey)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-1 h-5 theme-accent-bg rounded-full shadow-[0_0_8px_rgba(251,191,36,0.4)]" />
                                  <h4 className="text-xs font-black uppercase tracking-widest italic theme-accent-text">Fase de eliminación</h4>
                                </div>
                                {isElimExpanded ? <ChevronUp size={16} className="theme-primary-text" /> : <ChevronDown size={16} className="opacity-40" />}
                              </div>
                              {isElimExpanded && (
                                <div className="pl-6 space-y-6 animate-in slide-in-from-top-2 duration-200">
                                  {Object.entries(tData.elimination)
                                    .sort(([a], [b]) => {
                                      const order = ['8vos', '4tos', 'semi', '3er', 'final'];
                                      const idxA = order.findIndex(o => a.toLowerCase().includes(o));
                                      const idxB = order.findIndex(o => b.toLowerCase().includes(o));
                                      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                                      return idxA !== -1 ? -1 : (idxB !== -1 ? 1 : a.localeCompare(b));
                                    })
                                    .map(([rName, rMatches]: [string, any]) => (
                                      <div key={rName} className="space-y-4">
                                        <div className="flex items-center gap-3 mb-2">
                                          <span className="text-[10px] font-black uppercase tracking-[0.2em] theme-accent-text opacity-80">{rName.replace(' (V)', ' (vuelta)')}</span>
                                          <div className="h-px flex-1 bg-white/5" />
                                        </div>
                                        <div className={`${rMatches.length === 1 ? 'flex justify-center' : 'flex flex-wrap gap-3 justify-center'}`}>
                                          {rMatches.map((m: Match) => {
                                            if (m.team1Id === 'BYE' || m.team2Id === 'BYE') {
                                              const actualTeamId = m.team1Id === 'BYE' ? m.team2Id : m.team1Id;
                                              const actualTeam = getTeamObj(actualTeamId);
                                              return (
                                                <div key={m.id} className={`${rMatches.length === 1 ? 'w-full max-w-[460px]' : 'w-full xl:w-[calc(50%-6px)] max-w-[460px] shrink-0'} bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center justify-center text-center opacity-70 min-h-[160px]`}>
                                                  <Coffee size={24} className="opacity-40 mb-2" />
                                                  <span className="px-3 py-1 bg-white/10 text-white/60 rounded-md text-[10px] font-black uppercase tracking-widest mb-2 border border-white/5">Fecha Libre</span>
                                                  <p className="text-xs font-bold text-white/80 uppercase">{actualTeam?.name || 'DESCONOCIDO'}</p>
                                                  <p className="text-[10px] text-white/40 mt-1">No hay partido en esta ronda</p>
                                                </div>
                                              );
                                            }
                                            return (
                                              <div 
                                                key={m.id} 
                                                data-dispute-id={m.id} 
                                                className={`${rMatches.length === 1 ? 'w-full max-w-[460px]' : 'w-full xl:w-[calc(50%-6px)] max-w-[460px] shrink-0'} scroll-mt-32`}
                                              >
                                              <AdminPendingMatchCard m={m} t1={getTeamObj(m.team1Id) as Team} t2={getTeamObj(m.team2Id) as Team} players={players} onSave={handleSaveResult} onDelete={activeTab === 'archivadas' ? () => handleDeleteArchivedDispute(m.id) : () => handleDeletePendingMatch(m.id)} readOnly={activeTab === 'archivadas'} onMatchClick={onMatchClick} tournament={tournamentObj} onUpdateDateTime={handleUpdateDateTime} onPlayerAvatarClick={setSelectedPlayerId} />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          );
                        })()
                      )}

                      {Object.entries(tData.zones).map(([zName, zRounds]: [string, any]) => {
                        const zoneKey = `${tName}::${zName}`;
                        const isZoneExpanded = expandedBanners.includes(zoneKey);
                        const isZoneSelected = selectedBanner === zoneKey;
                        return (
                          <div key={zName} className="space-y-4">
                            <div
                              className={`px-5 py-3 rounded-xl flex items-center justify-between border cursor-pointer transition-all ${isZoneSelected ? 'theme-primary-border bg-theme-primary/10 shadow-[0_0_15px_rgba(var(--glow-rgb), 0.1)]' : 'bg-white/5 border-white/10 hover:bg-white/10 opacity-70 hover:opacity-100'}`}
                              onClick={() => toggleBanner(zoneKey)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-1 h-5 theme-primary-bg rounded-full shadow-[0_0_8px_rgba(var(--glow-rgb), 0.4)]" />
                                <h4 className="text-xs font-black uppercase tracking-widest opacity-80">Zona: {zName}</h4>
                              </div>
                              {isZoneExpanded ? <ChevronUp size={16} className="theme-primary-text" /> : <ChevronDown size={16} className="opacity-40" />}
                            </div>
                            {isZoneExpanded && (
                              <div className="pl-6 space-y-6 animate-in slide-in-from-top-2 duration-200">
                                {Object.entries(zRounds)
                                  .sort(([a], [b]) => {
                                    const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                                    const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                                    return numA - numB;
                                  })
                                  .map(([rName, rMatches]: [string, any]) => (
                                    <div key={rName} className="space-y-4">
                                      <div className="flex items-center gap-3 mb-2">
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] theme-primary-text opacity-80">{rName.replace(' (V)', ' (vuelta)')}</span>
                                        <div className="h-px flex-1 bg-white/5" />
                                      </div>
                                      <div className={`${rMatches.length === 1 ? 'flex justify-center' : 'flex flex-wrap gap-3 justify-center'}`}>
                                        {rMatches.map((m: Match) => {
                                          if (m.team1Id === 'BYE' || m.team2Id === 'BYE') {
                                            const actualTeamId = m.team1Id === 'BYE' ? m.team2Id : m.team1Id;
                                            const actualTeam = getTeamObj(actualTeamId);
                                            return (
                                              <div key={m.id} className={`${rMatches.length === 1 ? 'w-full max-w-[460px]' : 'w-full xl:w-[calc(50%-6px)] max-w-[460px] shrink-0'} bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center justify-center text-center opacity-70 min-h-[160px]`}>
                                                <Coffee size={24} className="opacity-40 mb-2" />
                                                <span className="px-3 py-1 bg-white/10 text-white/60 rounded-md text-[10px] font-black uppercase tracking-widest mb-2 border border-white/5">Fecha Libre</span>
                                                <p className="text-xs font-bold text-white/80 uppercase">{actualTeam?.name || 'DESCONOCIDO'}</p>
                                                <p className="text-[10px] text-white/40 mt-1">No hay partido en esta ronda</p>
                                              </div>
                                            );
                                          }
                                          return (
                                            <div 
                                              key={m.id} 
                                              data-dispute-id={m.id} 
                                              className={`${rMatches.length === 1 ? 'w-full max-w-[460px]' : 'w-full xl:w-[calc(50%-6px)] max-w-[460px] shrink-0'} scroll-mt-32`}
                                            >
                                              <AdminPendingMatchCard m={m} t1={getTeamObj(m.team1Id) as Team} t2={getTeamObj(m.team2Id) as Team} players={players} onSave={handleSaveResult} onDelete={activeTab === 'archivadas' ? () => handleDeleteArchivedDispute(m.id) : () => handleDeletePendingMatch(m.id)} readOnly={activeTab === 'archivadas'} onMatchClick={onMatchClick} tournament={tournamentObj} onUpdateDateTime={handleUpdateDateTime} onPlayerAvatarClick={setSelectedPlayerId} />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {Object.entries(tData.rounds || {}).length > 0 && Object.entries(tData.rounds)
                        .sort(([a], [b]) => {
                          const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                          const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                          return numA - numB;
                        })
                        .map(([rName, rMatches]: [string, any]) => (
                          <div key={rName} className="space-y-4">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">{rName.replace(' (V)', ' (vuelta)')}</span>
                              <div className="h-px flex-1 bg-white/5" />
                            </div>
                            <div className={`${rMatches.length === 1 ? 'flex justify-center' : 'flex flex-wrap gap-3 justify-center'}`}>
                              {rMatches.map((m: Match) => {
                                if (m.team1Id === 'BYE' || m.team2Id === 'BYE') {
                                  const actualTeamId = m.team1Id === 'BYE' ? m.team2Id : m.team1Id;
                                  const actualTeam = getTeamObj(actualTeamId);
                                  return (
                                    <div key={m.id} className={`${rMatches.length === 1 ? 'w-full max-w-[460px]' : 'w-full xl:w-[calc(50%-6px)] max-w-[460px] shrink-0'} bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center justify-center text-center opacity-70 min-h-[160px]`}>
                                      <Coffee size={24} className="opacity-40 mb-2" />
                                      <span className="px-3 py-1 bg-white/10 text-white/60 rounded-md text-[10px] font-black uppercase tracking-widest mb-2 border border-white/5">Fecha Libre</span>
                                      <p className="text-xs font-bold text-white/80 uppercase">{actualTeam?.name || 'DESCONOCIDO'}</p>
                                      <p className="text-[10px] text-white/40 mt-1">No hay partido en esta ronda</p>
                                    </div>
                                  );
                                }
                                return (
                                  <div 
                                    key={m.id} 
                                    data-dispute-id={m.id} 
                                    className={`${rMatches.length === 1 ? 'w-full max-w-[460px]' : 'w-full xl:w-[calc(50%-6px)] max-w-[460px] shrink-0'} scroll-mt-32`}
                                  >
                                    <AdminPendingMatchCard m={m} t1={getTeamObj(m.team1Id) as Team} t2={getTeamObj(m.team2Id) as Team} players={players} onSave={handleSaveResult} onDelete={activeTab === 'archivadas' ? () => handleDeleteArchivedDispute(m.id) : () => handleDeletePendingMatch(m.id)} readOnly={activeTab === 'archivadas'} onMatchClick={onMatchClick} tournament={tournamentObj} onUpdateDateTime={handleUpdateDateTime} onPlayerAvatarClick={setSelectedPlayerId} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      
                      {Object.keys(tData.elimination || {}).length === 0 && 
                       Object.keys(tData.zones || {}).length === 0 && 
                       Object.keys(tData.rounds || {}).length === 0 && (
                        <div className="flex flex-col items-center justify-center p-8 text-center bg-black/20 rounded-xl border border-white/5 mx-2 md:mx-4 mt-2 mb-2">
                          <CheckCircle2 size={32} className="text-white/20 mb-3" />
                          <p className="text-xs font-black uppercase tracking-widest text-white/50">Sin partidos pendientes por ahora</p>
                          <p className="text-[10px] text-white/30 mt-2">Los nuevos partidos aparecerán aquí</p>
                        </div>
                      )}

                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </>
      )}

      {/* Player Detail Modal */}
      {selectedPlayerId && (
        <PlayerDetailModal
          player={players.find(p => p.id === selectedPlayerId) || null}
          players={players}
          currentUserId={userId}
          currentUserProfile={{ role: userRole } as any}
          onMatchClick={onMatchClick || (() => {})}
          onPlayerClick={setSelectedPlayerId}
          onClose={() => setSelectedPlayerId(null)}
          config={config}
          forceShowContactInfo={true}
        />
      )}
    </div>
  );
}


export function AdminCategoryValidationSection({
  players,
  db,
  getCollectionPath,
  userId,
  userRole,
  sedesPublicas,
  setLocalSuccessMsg,
  adminClubId
}: {
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  userId: string;
  userRole: string;
  sedesPublicas: UserProfile[];
  setLocalSuccessMsg: (msg: string) => void;
  adminClubId?: string | null;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<{ player: Player; newCategory: string } | null>(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const [requests, setRequests] = useState<any[]>([]);

  const targetClubId = adminClubId || userId;

  useEffect(() => {
    if (userRole === 'superadmin') return;

    const q = query(
      collection(db, getCollectionPath('categoryRequests')),
      where('status', '==', 'pending'),
      where('targetClubId', '==', targetClubId)
    );
    const unsub = onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [db, getCollectionPath, targetClubId, userRole]);

  const categories = ['1ra', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va', 'Invitado'];

  const filteredPlayers = players.filter(p => 
    !p.isGuest && (
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.nickname && p.nickname.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  const handleCategoryChange = async (player: Player, newCategory: string) => {
    setUpdatingId(player.id);
    try {
      const clubProfile = sedesPublicas.find(u => u.uid === userId || (u as any).id === userId);
      const clubName = clubProfile?.name || 'Club';

      const playerRef = doc(db, getCollectionPath('players'), player.id);
      await updateDoc(playerRef, {
        category: newCategory,
        categoryValidated: true,
        validatedByCategoryBy: userRole === 'superadmin' ? 'superadmin' : 'club',
        validatedByCategoryClubName: userRole === 'superadmin' ? '' : clubName,
        validatedByCategoryClubId: userRole === 'superadmin' ? '' : userId,
        validatedByCategoryAt: Date.now()
      });

      // Send notification to the player
      const notificationBody = userRole === 'superadmin'
        ? `RPT Oficial te ha re-validado a ${newCategory} categoría.`
        : `El club ${clubName} te ha re-validado a ${newCategory} categoría.`;

      await addDoc(collection(db, 'notifications'), {
        userId: player.id,
        type: 'info',
        title: 'Categoría Actualizada',
        body: notificationBody,
        createdAt: Date.now(),
        read: false
      });

      setLocalSuccessMsg(`Categoría de ${player.name} actualizada a ${newCategory}`);
      setTimeout(() => setLocalSuccessMsg(''), 4000);
    } catch (e) {
      console.error(e);
      alert('Error al actualizar la categoría.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleApproveRequest = async (req: any) => {
    setUpdatingId(req.id);
    try {
      const clubProfile = sedesPublicas.find(u => u.uid === targetClubId || (u as any).id === targetClubId);
      const clubName = clubProfile?.name || 'Club';

      // 1. Update player
      const playerRef = doc(db, getCollectionPath('players'), req.playerId);
      await updateDoc(playerRef, {
        category: req.requestedCategory,
        categoryValidated: true,
        validatedByCategoryBy: 'club',
        validatedByCategoryClubName: clubName,
        validatedByCategoryClubId: targetClubId,
        validatedByCategoryAt: Date.now()
      });

      // 2. Update request
      const reqRef = doc(db, getCollectionPath('categoryRequests'), req.id);
      await updateDoc(reqRef, {
        status: 'accepted',
        resolvedAt: Date.now(),
        resolvedBy: userId
      });

      // 3. Notify player
      await addDoc(collection(db, 'notifications'), {
        userId: req.playerId,
        type: 'info',
        title: 'Solicitud de Categoría Aprobada',
        body: `El club ${clubName} ha aprobado tu solicitud. Tu nueva categoría es ${req.requestedCategory}.`,
        createdAt: Date.now(),
        read: false
      });

      setLocalSuccessMsg(`Solicitud aprobada: ${req.playerName} ahora es ${req.requestedCategory}`);
      setTimeout(() => setLocalSuccessMsg(''), 4000);
    } catch (e) {
      console.error(e);
      alert('Error al aprobar la solicitud.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex justify-center text-center mb-2">
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-widest text-white flex items-center justify-center gap-2">
            Validación de Categorías
          </h3>
        </div>

        <div className="bg-white/[0.03] backdrop-blur-sm border border-white/5 rounded-xl p-4 text-white text-center shadow-lg">
          <p className="text-xs leading-relaxed opacity-90 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Info className="shrink-0 text-white/50" size={16} />
            <span>Las validaciones de categorías y el nombre club validador, son siempre <strong>PÚBLICOS</strong> y visibles por todos. Cada club tiene la facultad de ascender o descender de categoría a un jugador según su propia evaluación técnica.</span>
          </p>
        </div>

        {requests.length > 0 && (
          <div className="space-y-3 mt-4 mb-2">
            <h4 className="text-sm font-black uppercase text-theme-primary flex items-center gap-2">
              <AlertTriangle size={14} /> Solicitudes Pendientes ({requests.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {requests.map(req => (
                <div key={req.id} className="bg-slate-900/80 border border-theme-primary/30 rounded-xl p-4 flex flex-col gap-3 shadow-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <span className="text-white font-bold text-sm">{req.playerName}</span>
                      <span className="text-white/50 text-[10px] uppercase font-black">De: {req.currentCategory} &rarr; A: {req.requestedCategory}</span>
                    </div>
                    <span className="text-[9px] uppercase font-black bg-theme-primary/10 text-theme-primary px-2 py-1 rounded-md">
                      Pendiente
                    </span>
                  </div>
                  <button
                    onClick={() => handleApproveRequest(req)}
                    disabled={updatingId === req.id}
                    className="w-full theme-primary-bg text-black font-black uppercase text-xs py-2.5 rounded-lg hover:scale-[1.02] active:scale-95 transition-all shadow-md shadow-theme-primary/20 flex justify-center items-center gap-2 disabled:opacity-50"
                  >
                    {updatingId === req.id ? 'Aprobando...' : `Aprobar solicitud: ${req.requestedCategory}`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Minimalist Search Bar */}
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
          <input
            type="text"
            placeholder="Buscar jugador por nombre o apodo..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setVisibleCount(5);
            }}
            className="w-full bg-black/20 border border-white/10 focus:border-white/20 rounded-xl py-3 pl-12 pr-6 outline-none text-sm text-white transition-all duration-300 placeholder:text-white/30"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs text-white/80 border-collapse">
          <thead>
            <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider text-[10px] font-black">
              <th className="py-3 px-4">Jugador</th>
              <th className="py-3 px-4">Categoría Actual</th>
              <th className="py-3 px-4">Modificar Categoría</th>
              <th className="py-3 px-4">Estado de Validación</th>
            </tr>
          </thead>
          <tbody>
            {filteredPlayers.slice(0, visibleCount).map(p => {
              const valBy = (p as any).validatedByCategoryBy;
              const valClub = (p as any).validatedByCategoryClubName;
              return (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="py-3 px-4 font-bold">{p.name} {p.nickname && <span className="opacity-40">({p.nickname})</span>}</td>
                  <td className="py-3 px-4">
                    <span className="px-2.5 py-0.5 bg-white/5 border border-white/10 rounded-lg text-theme-primary font-black uppercase">{p.category || 'Sin cat'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <select
                      value={p.category}
                      disabled={updatingId === p.id}
                      onChange={(e) => {
                        setPendingChange({ player: p, newCategory: e.target.value });
                      }}
                      className="bg-black/60 border border-white/10 text-white text-xs rounded-lg px-2.5 py-1.5 focus:border-theme-primary/50 outline-none uppercase font-bold"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 px-4">
                    {valBy === 'club' && valClub ? (
                      <span className="text-amber-400 font-bold uppercase tracking-wider text-[9px] flex items-center gap-1">🌟 Club: {valClub}</span>
                    ) : valBy === 'superadmin' ? (
                      <span className="text-amber-400 font-bold uppercase tracking-wider text-[9px] flex items-center gap-1">🌟 RPT Oficial</span>
                    ) : p.isValidated ? (
                      <span className="text-amber-400 font-bold uppercase tracking-wider text-[9px] flex items-center gap-1">🌟 RPT Oficial</span>
                    ) : (
                      <span className="text-white/30 uppercase text-[9px] font-bold">Sin Validar</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {filteredPlayers.length === 0 && (
              <tr>
                <td colSpan={4} className="py-8 text-center text-white/30 font-medium">Ningún jugador encontrado</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Button to load more players */}
      {filteredPlayers.length > visibleCount && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setVisibleCount(prev => prev + 10)}
            className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-lg"
          >
            Ver más jugadores
          </button>
        </div>
      )}

      {/* Modal de Confirmación Premium */}
      {pendingChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[#1a1c23] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-6 text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto border border-amber-500/20">
              <AlertTriangle className="text-amber-500 animate-pulse" size={28} />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-lg font-black uppercase tracking-wider text-white">¿Confirmar Cambio?</h3>
              <p className="text-xs text-white/60 leading-relaxed">
                ¿Deseas cambiar la categoría de <strong className="text-white">{pendingChange.player.name}</strong> de <strong className="text-theme-primary">"{pendingChange.player.category || 'Sin Categoría'}"</strong> a <strong className="text-emerald-400">"{pendingChange.newCategory}"</strong>?
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPendingChange(null)}
                className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  handleCategoryChange(pendingChange.player, pendingChange.newCategory);
                  setPendingChange(null);
                }}
                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg shadow-emerald-500/20 active:scale-95 cursor-pointer"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

